(() => {
  const HANDOFF_KEY = 'wl-project-plan-handoff-v1';
  const STATE_PREFIX = 'wl-project-planner-state-v1:';

  if (customElements.get('wl-project-planner')) return;

  class WlProjectPlanner extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.addEventListener('click', this.handleClick);
        this.addEventListener('input', this.handleChange);
        this.addEventListener('change', this.handleChange);
        this.productQuantity?.addEventListener('change', this.handleChange);
        window.addEventListener('pageshow', this.handlePageShow);
        this.subscribeToVariantChanges();
        this.syncFromProductForm();
        this.update();
        return;
      }
      this.initialized = true;
      this.action = this.querySelector('[data-project-action]');
      this.actionLabel = this.querySelector('[data-project-action-label]');
      this.status = this.querySelector('[data-project-status]');
      this.summary = this.querySelector('[data-project-summary]');
      this.primaryQuantity = this.querySelector('[data-project-primary-quantity]');
      this.productForm = document.getElementById(this.dataset.productFormId);
      this.productQuantity = document.getElementById(this.dataset.productQuantityId);
      this.lines = Array.from(this.querySelectorAll('[data-project-line]'));
      this.busy = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('input', this.handleChange);
      this.addEventListener('change', this.handleChange);
      this.productQuantity?.addEventListener('change', this.handleChange);
      window.addEventListener('pageshow', this.handlePageShow);

      this.subscribeToVariantChanges();

      this.disableDuplicateLines();
      this.syncFromProductForm();
      this.restoreState();
      this.syncToProductForm();
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('input', this.handleChange);
      this.removeEventListener('change', this.handleChange);
      this.productQuantity?.removeEventListener('change', this.handleChange);
      window.removeEventListener('pageshow', this.handlePageShow);
      this.variantChangeUnsubscriber?.();
      this.variantChangeUnsubscriber = undefined;
    }

    subscribeToVariantChanges() {
      if (this.variantChangeUnsubscriber || typeof PUB_SUB_EVENTS === 'undefined' || typeof subscribe !== 'function') return;
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
        if (data?.variant?.id) this.syncPrimaryVariant(data.variant);
      });
    }

    handlePageShow() {
      this.syncFromProductForm();
      this.update();
    }

    handleClick(event) {
      const primaryButton = event.target.closest('[data-project-primary-change]');
      if (primaryButton) {
        event.preventDefault();
        this.changeQuantity(this.primaryQuantity, primaryButton.dataset.projectPrimaryChange);
        this.syncToProductForm();
        this.update();
        return;
      }

      const lineButton = event.target.closest('[data-project-quantity-change]');
      if (lineButton) {
        event.preventDefault();
        const input = lineButton.closest('[data-project-line]')?.querySelector('[data-project-quantity]');
        this.changeQuantity(input, lineButton.dataset.projectQuantityChange);
        this.update();
        return;
      }

      if (event.target.closest('[data-project-action]')) {
        event.preventDefault();
        this.submit();
      }
    }

    handleChange(event) {
      if (event.target === this.productQuantity) {
        this.syncFromProductForm();
      } else if (event.target === this.primaryQuantity) {
        this.normalizeQuantity(this.primaryQuantity);
        this.syncToProductForm();
      } else if (event.target.matches('[data-project-quantity]')) {
        this.normalizeQuantity(event.target);
      } else if (event.target.matches('[data-project-variant]')) {
        this.syncLineVariant(event.target.closest('[data-project-line]'));
        this.disableDuplicateLines();
      }

      this.update();
    }

    get primaryVariantInput() {
      this.productForm = this.productForm || document.getElementById(this.dataset.productFormId);
      return this.productForm?.querySelector('input[name="id"]');
    }

    normalizeQuantity(input) {
      if (!input) return 1;
      const min = Math.max(1, Number.parseInt(input.min || '1', 10) || 1);
      const max = Math.min(9999, Number.parseInt(input.max || '9999', 10) || 9999);
      const value = Math.min(max, Math.max(min, Number.parseInt(input.value || String(min), 10) || min));
      input.value = String(value);
      return value;
    }

    changeQuantity(input, direction) {
      if (!input) return;
      const current = this.normalizeQuantity(input);
      const step = Math.max(1, Number.parseInt(input.step || '1', 10) || 1);
      input.value = String(current + (Number(direction) < 0 ? -step : step));
      this.normalizeQuantity(input);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    syncFromProductForm() {
      if (this.productQuantity) this.primaryQuantity.value = this.productQuantity.value || '1';
      this.normalizeQuantity(this.primaryQuantity);
      const input = this.primaryVariantInput;
      if (input?.value) this.dataset.primaryVariantId = input.value;
    }

    syncToProductForm() {
      if (!this.productQuantity) return;
      this.productQuantity.value = String(this.normalizeQuantity(this.primaryQuantity));
      this.productQuantity.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncPrimaryVariant(variant) {
      this.dataset.primaryVariantId = String(variant.id || '');
      this.dataset.primaryPrice = String(variant.price || 0);
      this.dataset.primarySku = variant.sku || '';
      if (this.dataset.primaryCommerceMode !== 'freight_quote') {
        this.dataset.primaryCommerceMode = variant.available !== false && Number(variant.price || 0) > 0 ? 'cart' : 'unsupported';
      }
      const sku = this.querySelector('[data-project-primary-sku]');
      if (sku) sku.textContent = variant.sku ? `SKU ${variant.sku}` : 'SKU not provided';
      this.disableDuplicateLines();
      window.setTimeout(() => {
        this.productForm = document.getElementById(this.dataset.productFormId);
        this.productQuantity = document.getElementById(this.dataset.productQuantityId);
        this.syncFromProductForm();
        this.update();
      }, 0);
    }

    selectedVariantData(line) {
      const control = line.querySelector('[data-project-variant]');
      const option = control?.tagName === 'SELECT' ? control.selectedOptions[0] : control;
      return {
        id: String(option?.value || ''),
        price: Number(option?.dataset.price || 0),
        sku: option?.dataset.sku || '',
        available: option?.dataset.available === 'true',
      };
    }

    syncLineVariant(line) {
      if (!line) return;
      const variant = this.selectedVariantData(line);
      const sku = line.querySelector('[data-project-sku]');
      const state = line.querySelector('[data-project-item-state]');
      const mode = line.dataset.commerceMode;
      if (sku) sku.textContent = variant.sku ? `SKU ${variant.sku}` : 'SKU not provided';
      if (!state) return;
      if (mode === 'freight_quote') state.textContent = 'Freight quote required';
      else if (mode === 'quote_only') state.textContent = 'Custom Pricing review required';
      else if (!variant.available) state.textContent = 'Currently unavailable';
      else if (variant.price <= 0) state.textContent = 'Price unavailable — review required';
      else state.textContent = `${this.formatMoney(variant.price)} each`;
    }

    disableDuplicateLines() {
      const seen = new Set([String(this.dataset.primaryVariantId || '')].filter(Boolean));
      this.lines.forEach((line) => {
        const include = line.querySelector('[data-project-include]');
        const variant = this.selectedVariantData(line);
        const duplicate = Boolean(variant.id && seen.has(variant.id));
        const mode = line.dataset.commerceMode;
        const baseSelectable = mode === 'quote_only' || mode === 'freight_quote'
          ? Boolean(variant.id)
          : Boolean(variant.id && variant.available && variant.price > 0);
        if (variant.id) seen.add(variant.id);
        line.toggleAttribute('data-project-duplicate', duplicate);
        if (include) {
          include.disabled = duplicate || !baseSelectable;
          if (include.disabled) include.checked = false;
        }
        if (duplicate) {
          const state = line.querySelector('[data-project-item-state]');
          if (state) state.textContent = 'Already represented in this project list';
        } else {
          this.syncLineVariant(line);
        }
      });
    }

    selectedLines() {
      return this.lines.filter((line) => line.querySelector('[data-project-include]')?.checked);
    }

    projectLines() {
      const quantity = this.normalizeQuantity(this.primaryQuantity);
      const lines = [{
        handle: this.dataset.productHandle,
        productId: String(this.dataset.productId || ''),
        variantId: String(this.dataset.primaryVariantId || ''),
        sku: this.dataset.primarySku || '',
        quantity,
        commerceMode: this.dataset.primaryCommerceMode || 'unsupported',
        source: 'primary',
        title: 'Current product',
      }];

      this.selectedLines().forEach((line) => {
        const variant = this.selectedVariantData(line);
        lines.push({
          handle: line.dataset.handle || '',
          productId: String(line.dataset.productId || ''),
          variantId: variant.id,
          sku: variant.sku,
          quantity: this.normalizeQuantity(line.querySelector('[data-project-quantity]')),
          commerceMode: line.dataset.commerceMode || 'unsupported',
          source: 'optional_project_extra',
          title: line.querySelector('.wl-project-planner__item-heading label')?.textContent?.trim() || 'Optional project item',
        });
      });

      return lines;
    }

    requiresQuote(lines) {
      return lines.some((line) => line.commerceMode !== 'cart');
    }

    update() {
      const selected = this.selectedLines();
      const quantity = this.normalizeQuantity(this.primaryQuantity);
      const lines = this.projectLines();
      const quote = this.requiresQuote(lines);
      if (this.summary) {
        const optionalText = selected.length === 1 ? '1 optional item' : `${selected.length} optional items`;
        this.summary.textContent = `Current product quantity ${quantity} · ${optionalText}`;
      }
      if (this.actionLabel) this.actionLabel.textContent = quote ? 'Send list to Custom Pricing' : 'Add project items to cart';
      this.saveState();
    }

    setBusy(value) {
      this.busy = value;
      if (!this.action) return;
      this.action.disabled = value;
      this.action.setAttribute('aria-disabled', String(value));
      this.action.classList.toggle('loading', value);
      this.action.querySelector('.loading__spinner')?.classList.toggle('hidden', !value);
      this.setAttribute('aria-busy', String(value));
    }

    showStatus(message, type = 'info') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.status = type;
    }

    async submit() {
      if (this.busy) return;
      const lines = this.projectLines();
      if (!this.validVariantId(lines[0]?.variantId)) {
        this.showStatus('Choose an available product option before continuing.', 'error');
        return;
      }

      if (this.requiresQuote(lines)) {
        this.handoffToCustomPricing(lines);
        return;
      }

      await this.addToCart(lines);
    }

    validVariantId(value) {
      return /^\d+$/.test(String(value || ''));
    }

    async addToCart(lines) {
      const unique = new Map();
      lines.forEach((line) => {
        if (!this.validVariantId(line.variantId) || line.commerceMode !== 'cart') return;
        if (unique.has(line.variantId)) return;
        unique.set(line.variantId, line);
      });

      const cartSurface = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const successes = [];
      const failures = [];
      let latestCartData = null;
      this.setBusy(true);
      this.showStatus(`Adding ${unique.size} project line${unique.size === 1 ? '' : 's'}…`);

      for (const line of unique.values()) {
        try {
          const body = { items: [{ id: Number(line.variantId), quantity: line.quantity }] };
          if (cartSurface?.getSectionsToRender) {
            body.sections = cartSurface.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
          }
          const response = await fetch(this.dataset.cartAddUrl || window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || `Cart returned ${response.status}`);
          successes.push(line);
          latestCartData = data;
        } catch (error) {
          failures.push({ line, message: error?.message || 'Unknown cart error' });
        }
      }

      try {
        if (latestCartData && cartSurface?.renderContents) {
          cartSurface.setActiveElement?.(this.action);
          cartSurface.renderContents(latestCartData);
        }
      } catch (error) {
        console.error(error);
      }

      this.setBusy(false);
      if (!failures.length) {
        this.showStatus(`${successes.length} project line${successes.length === 1 ? '' : 's'} added to cart.`);
        return;
      }

      const failedNames = failures.map(({ line }) => line.title).join(', ');
      if (successes.length) {
        this.showStatus(`${successes.length} line${successes.length === 1 ? '' : 's'} added. Not added: ${failedNames}. Review the cart before retrying.`, 'error');
      } else {
        this.showStatus(`Nothing was added. Check these lines and try again: ${failedNames}.`, 'error');
      }
    }

    handoffToCustomPricing(lines) {
      const payload = {
        schemaVersion: 1,
        source: 'project_planner',
        sourceProduct: this.dataset.sourceProduct,
        createdAt: new Date().toISOString(),
        lines: lines.map((line) => ({
          handle: line.handle,
          productId: line.productId,
          variantId: line.variantId,
          sku: line.sku,
          quantity: line.quantity,
          commerceMode: line.commerceMode,
          source: line.source,
        })),
      };

      try {
        window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(payload));
      } catch (_unusedError) {
        this.showStatus('Your browser could not preserve the project list. Open Custom Pricing and add the products manually.', 'error');
        return;
      }

      const url = new URL(this.dataset.customPricingUrl || '/pages/custom-pricing-program', window.location.origin);
      url.searchParams.set('project_plan', '1');
      url.searchParams.set('source_product', this.dataset.sourceProduct || '');
      url.hash = 'custom-pricing-request';
      window.location.assign(`${url.pathname}${url.search}${url.hash}`);
    }

    stateKey() {
      return `${STATE_PREFIX}${this.dataset.productHandle || this.dataset.productId || 'product'}`;
    }

    saveState() {
      const state = {
        primaryQuantity: this.normalizeQuantity(this.primaryQuantity),
        selections: this.lines.map((line) => ({
          handle: line.dataset.handle || '',
          selected: Boolean(line.querySelector('[data-project-include]')?.checked),
          variantId: this.selectedVariantData(line).id,
          quantity: this.normalizeQuantity(line.querySelector('[data-project-quantity]')),
        })),
      };
      try {
        window.sessionStorage.setItem(this.stateKey(), JSON.stringify(state));
      } catch (_unusedError) {
        // Storage is an enhancement; core controls remain usable without it.
      }
    }

    restoreState() {
      try {
        const state = JSON.parse(window.sessionStorage.getItem(this.stateKey()) || 'null');
        if (!state) return;
        if (state.primaryQuantity) this.primaryQuantity.value = String(state.primaryQuantity);
        (state.selections || []).forEach((saved) => {
          const line = this.lines.find((candidate) => candidate.dataset.handle === saved.handle);
          if (!line) return;
          const variant = line.querySelector('[data-project-variant]');
          if (variant?.tagName === 'SELECT' && Array.from(variant.options).some((option) => option.value === String(saved.variantId))) {
            variant.value = String(saved.variantId);
          }
          const quantity = line.querySelector('[data-project-quantity]');
          if (quantity) quantity.value = String(saved.quantity || 1);
          const include = line.querySelector('[data-project-include]');
          if (include && !include.disabled) include.checked = Boolean(saved.selected);
          this.syncLineVariant(line);
        });
      } catch (_unusedError) {
        // Ignore malformed or unavailable session state.
      }
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney && window.theme?.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(Number(cents || 0) / 100);
    }
  }

  customElements.define('wl-project-planner', WlProjectPlanner);
})();
